import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// Importación del pilar del Contexto de Datos Maestros (Canvas)
import { useSegurosData } from '../../../../context/SegurosDataContext';
import { normalizeString } from '../../../../utils/segurosUtils';

// Componentes Hijos
import ImportadorTab from './ImportadorTab';
import ImportadorContratosTab from './ImportadorContratosTab';
import RemesasTab from './RemesasTab';
import ReportesTab from './ReportesTab';

/**
 * AdminLiquidaciones - Orquestador central del panel financiero de IB Brokers.
 * Refacturado para consumir datos maestros directamente desde el SegurosDataContext,
 * optimizando el consumo de red de Firebase a 0 consultas repetidas.
 */
const AdminLiquidaciones = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('importar');
    
    // --- CONSUMO DE CONTEXTO MAESTRO GLOBAL ---
    const { dbMasters } = useSegurosData();

    /**
     * useMemo - Construcción de mapas indexados de alta velocidad para matching de Excel.
     * Genera indexaciones cruzadas tanto por ID como por Nombre normalizado en mayúsculas.
     */
    const maestrosUnificados = useMemo(() => {
        const canales = dbMasters?.canales || [];
        const subcanales = dbMasters?.subcanales || [];

        const mapaCanales = {};
        const mapaSubcanales = {};
        const mapaSubcanalesNombre = {};

        canales.forEach(c => {
            if (c.id) mapaCanales[c.id] = c;
            const nombreNorm = normalizeString(c.nombreDisplay || c.nombre || '');
            if (nombreNorm) mapaCanales[nombreNorm] = c;
        });

        subcanales.forEach(s => {
            if (s.id) mapaSubcanales[s.id] = s;
            const nombreNorm = normalizeString(s.nombre || '');
            if (nombreNorm) {
                mapaSubcanalesNombre[nombreNorm] = s;
            }
        });

        return {
            canales,
            subcanales,
            mapaCanales,
            mapaSubcanales,
            mapaSubcanalesNombre,
            companias: dbMasters?.companias || [],
            riesgos: dbMasters?.riesgos || []
        };
    }, [dbMasters]);

    return (
        <div className="min-h-screen bg-gray-50 p-8 md:p-12 font-sans text-gray-800 text-left">
            <div className="max-w-[95%] mx-auto">
                
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 no-print gap-4">
                    <div className="text-left">
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight uppercase">Liquidación de Comisiones</h1>
                        <p className="text-gray-500 mt-1 font-medium">Importación masiva automatizada y control de distribución de saldos comerciales.</p>
                    </div>
                    <button 
                        onClick={() => navigate('/AdminDashboard')} 
                        className="bg-white border border-gray-300 text-gray-700 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:text-indigo-600 transition text-xs uppercase tracking-widest active:scale-95 transform"
                    >
                        Volver al panel
                    </button>
                </div>

                {/* Navegación por Tabs */}
                <div className="flex space-x-1 bg-gray-200 p-1 rounded-xl w-fit mb-6 no-print select-none font-bold">
                    <button onClick={() => setActiveTab('importar')} className={`px-6 py-2 rounded-lg text-xs uppercase transition-all ${activeTab === 'importar' ? 'bg-white text-indigo-600 shadow-sm font-black' : 'text-gray-500 hover:text-gray-700'}`}>Importar Pólizas</button>
                    <button onClick={() => setActiveTab('importar-art')} className={`px-6 py-2 rounded-lg text-xs uppercase transition-all ${activeTab === 'importar-art' ? 'bg-white text-indigo-600 shadow-sm font-black' : 'text-gray-500 hover:text-gray-700'}`}>Importar ART</button>
                    <button onClick={() => setActiveTab('remesas')} className={`px-6 py-2 rounded-lg text-xs uppercase transition-all ${activeTab === 'remesas' ? 'bg-white text-indigo-600 shadow-sm font-black' : 'text-gray-500 hover:text-gray-700'}`}>Historial Remesas</button>
                    <button onClick={() => setActiveTab('gestion')} className={`px-6 py-2 rounded-lg text-xs uppercase transition-all ${activeTab === 'gestion' ? 'bg-white text-indigo-600 shadow-sm font-black' : 'text-gray-500 hover:text-gray-700'}`}>Gestión y Reportes</button>
                </div>

                {/* Área de Trabajo */}
                <div className="animate-fade-in text-left">
                    {activeTab === 'importar' && <ImportadorTab maestros={maestrosUnificados} setActiveTab={setActiveTab} />}
                    {activeTab === 'importar-art' && <ImportadorContratosTab maestros={maestrosUnificados} setActiveTab={setActiveTab} />}
                    {activeTab === 'remesas' && <RemesasTab />}
                    {activeTab === 'gestion' && <ReportesTab maestros={maestrosUnificados} />}
                </div>

            </div>
            
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body { background: white; font-family: 'Arial', sans-serif; -webkit-print-color-adjust: exact; }
                    @page { margin: 1cm; size: A4; }
                }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default AdminLiquidaciones;